/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import axios from 'axios';

import { objectToQueryString, splitArrays } from './utils';

let baseUrl;

if (process.env.NODE_ENV === 'development') {
  const apiUrl = process.env.REACT_APP_BIKE_LOCATOR_API_URL;
  baseUrl = `${apiUrl}/api/v1`;
} else {
  baseUrl = '/api/v1';
}

const setAuthToken = (config) => {
  const token = localStorage.getItem('token');
  if (token) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = token;
  }
  return config;
};

const axiosInstance = axios.create({ withCredentials: true });
axiosInstance.interceptors.request.use(setAuthToken);

const refreshAuthToken = async (error) => {
  const originalRequest = error.config;
  const refreshToken = localStorage.getItem('refreshToken');
  if ((error.response.status === 401 || error.response.status === 403) && refreshToken) {
    try {
      const url = `${baseUrl}/user/refreshToken?refreshToken=${refreshToken}`;
      const res = await axiosInstance
        .get(url)
        .then((response) => response.data);
      const token = res.res.data.access_token;
      localStorage.setItem('token', token);
      originalRequest.headers.Authorization = token;
      return axios(originalRequest);
    } catch (err) {
      localStorage.removeItem('refreshToken');
    }
  }
  return Promise.reject(error);
};
axiosInstance.interceptors.response.use((response) => response, refreshAuthToken);

const user = {
  name: localStorage.getItem('userFullName'),
  email: localStorage.getItem('username'),
};

const userId = localStorage.getItem('userId');

export default {
  async addToAppCenterIOS(emails) {
    const url = `${baseUrl}/appCenter/ios`;
    return axiosInstance.post(url, { emails });
  },
  async addToAppCenterAndroid(emails) {
    const url = `${baseUrl}/appCenter/android`;
    return axiosInstance.post(url, { emails });
  },
  async resendEmailAppCenterIOS(emails) {
    const url = `${baseUrl}/appCenter/ios/resend`;
    return axiosInstance.post(url, { emails });
  },
  async resendEmailAppCenterAndroid(emails) {
    const url = `${baseUrl}/appCenter/android/resend`;
    return axiosInstance.post(url, { emails });
  },
  async deleteEmailAppCenterIOS(emails) {
    const url = `${baseUrl}/appCenter/ios/delete`;
    return axiosInstance.post(url, { emails });
  },
  async deleteEmailAppCenterAndroid(emails) {
    const url = `${baseUrl}/appCenter/android/delete`;
    return axiosInstance.post(url, { emails });
  },
  async getSettings() {
    const url = `${baseUrl}/config/`;
    const res = await axiosInstance
      .get(url)
      .then((response) => response.data);
    return res;
  },

  async getPublicConfig() {
    const url = `${baseUrl}/publicConfig`;
    const res = await axiosInstance
      .get(url)
      .then((response) => response.data);
    return res;
  },
  async checkToken(token) {
    const url = `${baseUrl}/user/token?token=${token}`;
    const res = await axiosInstance
      .get(url)
      .then((response) => response.data);
    return res;
  },

  async checkTokenMicrosoft(idToken) {
    const url = `${baseUrl}/user/tokenMicrosoft`;
    const res = await axiosInstance
      .post(url, { idToken })
      .then((response) => response.data);
    return res;
  },

  async getUserFromEmail(email) {
    const url = `${baseUrl}/wranglers/byEmail?email=${email}`;
    const res = await axiosInstance
      .get(url)
      .then((response) => response.data);
    return res;
  },

  async getMarkets(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/markets?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async getMarketBrands() {
    const data = await axiosInstance
      .get(`${baseUrl}/markets/brands`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async updateMarket(newMarket, marketId) {
    return axiosInstance.put(`${baseUrl}/markets/${marketId}`, newMarket);
  },

  async getTargetDeployments(marketId) {
    const data = await axiosInstance
      .get(`${baseUrl}/targetDeployments/${marketId}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        throw error;
      });
    return data;
  },

  async createTargetDeployment(args) {
    const data = await axiosInstance
      .post(`${baseUrl}/targetDeployments/`, args)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        throw error;
      });
    return data;
  },

  async updateTargetDeployment(targetId, target, effectiveOn) {
    const data = await axiosInstance
      .put(`${baseUrl}/targetDeployments/${targetId}`, { target, effectiveOn })
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        throw error;
      });
    return data;
  },

  async deleteTargetDeployment(targetId) {
    const data = axiosInstance
      .delete(`${baseUrl}/targetDeployments/${targetId}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error('got an API error', error);
        throw error;
      });
    return data;
  },

  async getRideReportData(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    return axiosInstance
      .get(`${baseUrl}/reports/rideReport?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  async getRideReportCsv(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    return axiosInstance
      .get(`${baseUrl}/reports/rideReport?${qs}`, { responseType: 'blob' });
  },

  async getRepairTicketReport(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/reports/repairTickets?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async getRepairTicketsForAggregation(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/reports/repairTicketsForAggregation?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async getInventoryReport(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/reports/inventory?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async getDeploymentEvents(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/reports/deploymentEvents?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async getVehicleHistory(vehicleId) {
    return axiosInstance.get(`${baseUrl}/vehicles/${vehicleId}/history`)
      .then((response) => response.data);
  },

  async getTasks(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/tasks?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async getOneTask(taskId) {
    const data = await axiosInstance
      .get(`${baseUrl}/tasks/${taskId}`)
      .then((response) => response.data);
    return data;
  },

  async getVehicleTasks(vehicleIds) {
    return axiosInstance
      .post(`${baseUrl}/tasks/vehicle?`, { vehicleIds })
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
  },

  async getDeploymentTasks(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/reports/deployments?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async createTasksForVehicles(taskData, vehicleIds) {
    return axiosInstance.post(`${baseUrl}/tasks/bulkForVehicles`, {
      taskData, vehicleIds, user, userId,
    });
  },

  async createTasksForCases(taskData, caseData) {
    return axiosInstance.post(`${baseUrl}/tasks/bulkForCases`, {
      taskData, caseData, user, userId,
    });
  },

  async createTask(newTask) {
    return axiosInstance.post(`${baseUrl}/tasks/`, { newTask, user, userId });
  },

  async updateTask(newTask, taskId) {
    return axiosInstance.put(`${baseUrl}/tasks/${taskId}`, { newTask, user, userId });
  },

  async bulkUpdateTaskStatus(arrayOfTaskIds) {
    return axiosInstance.post(`${baseUrl}/tasks/batch`, { arrayOfTaskIds, userId });
  },

  async bulkUpdateTaskOwner(taskIds, ownerId) {
    return axiosInstance.post(`${baseUrl}/tasks/batchReassign`, { taskIds, ownerId });
  },

  async getVehicles(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/vehicles?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async getDashboardData(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/markets/dashboard?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async getOneVehicle(vehicleId) {
    const data = await axiosInstance
      .get(`${baseUrl}/vehicles/${vehicleId}`)
      .then((response) => response.data);
    return data;
  },

  async getVehicleActivityDates(query) {
    const qs = objectToQueryString(query);
    const data = await axiosInstance
      .get(`${baseUrl}/vehicles/activityDates?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async getRideWithRouteByTripId(tripId) {
    return axiosInstance.get(`${baseUrl}/rides/route/${tripId}`);
  },

  async getVehicleTripsById(vehicleId) {
    const data = await axiosInstance
      .get(`${baseUrl}/rides/${vehicleId}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async updateVehicle(newVehicle, vehicleId) {
    return axiosInstance.put(`${baseUrl}/vehicles/${vehicleId}`, { newVehicle });
  },

  async bulkUpdateVehicleStatus(vehicleExtIds, deploymentStatus, possessionStatus) {
    return axiosInstance.post(`${baseUrl}/vehicles/status`, { vehicleExtIds, deploymentStatus, possessionStatus });
  },

  async unlockVehicle(vehicleId) {
    const data = await axiosInstance
      .get(`${baseUrl}/vehicles/${vehicleId}/unlock`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async lockVehicle(vehicleId) {
    const data = await axiosInstance
      .get(`${baseUrl}/vehicles/${vehicleId}/lock`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async ringVehicle(vehicleId) {
    return axiosInstance.get(`${baseUrl}/vehicles/${vehicleId}/ring`);
  },

  async startRingingVehicle(vehicleId) {
    return axiosInstance.get(`${baseUrl}/vehicles/${vehicleId}/startRingingVehicle`);
  },

  async stopRingingVehicle(vehicleId) {
    return axiosInstance.get(`${baseUrl}/vehicles/${vehicleId}/stopRingingVehicle`);
  },

  async getFieldReports(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/fieldReports?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async updateFieldReport(newFieldReport, fieldReportId) {
    return axiosInstance.put(`${baseUrl}/fieldReports/${fieldReportId}`, newFieldReport);
  },

  async bulkUpdateFieldReportStatus(arrayOfFieldReportIds) {
    return axiosInstance.post(`${baseUrl}/fieldReports/batch`, arrayOfFieldReportIds);
  },

  async getLocations(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/locations?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async createLocation(location) {
    return axiosInstance.post(`${baseUrl}/locations/`, location);
  },

  async updateLocation(newLocation, locationId) {
    return axiosInstance.put(`${baseUrl}/locations/${locationId}`, newLocation);
  },

  async bulkUploadLocations(marketId, hubData, overwrite) {
    return axiosInstance.post(`${baseUrl}/locations/bulkUpload`, { marketId, hubData, overwrite });
  },

  async bulkEditLocations(locationIds, newHubCapacity, newHubStatus) {
    return axiosInstance.post(`${baseUrl}/locations/batch/bulkEditHubs`, { locationIds, newHubCapacity, newHubStatus });
  },

  async getZones(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/zones?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async createZone(zone) {
    return axiosInstance.post(`${baseUrl}/zones/`, zone);
  },

  async updateZone(newZone, zoneId) {
    return axiosInstance.put(`${baseUrl}/zones/${zoneId}`, newZone);
  },

  async updateMultipleZones(zones) {
    return axiosInstance.post(`${baseUrl}/zones/locations`, zones);
  },

  async getVehicleZones(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    return axiosInstance
      .get(`${baseUrl}/vehicleZones?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        return error;
      });
  },

  async createVehicleZone(zone) {
    return axiosInstance
      .post(`${baseUrl}/vehicleZones/`, zone)
      .then((response) => response.data)
      .catch((error) => {
        console.error(`got error creating vehicle zone ${error}`, error);
        return error;
      });
  },

  async updateVehicleZone(zoneId, newZone) {
    return axiosInstance
      .put(`${baseUrl}/vehicleZones/${zoneId}`, newZone)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        return error;
      });
  },

  async deleteVehicleZone(zoneId) {
    return axiosInstance.delete(`${baseUrl}/vehicleZones/${zoneId}`);
  },

  async getPickupZones(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    return axiosInstance
      .get(`${baseUrl}/pickupZones?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        return error;
      });
  },

  async createPickupZone(zone) {
    return axiosInstance.post(`${baseUrl}/pickupZones/`, zone);
  },

  async updatePickupZone(zoneId, newZone) {
    return axiosInstance.put(`${baseUrl}/pickupZones/${zoneId}`, newZone);
  },

  async deletePickupZone(zoneId) {
    return axiosInstance.delete(`${baseUrl}/pickupZones/${zoneId}`);
  },

  async bulkUploadPickupZones(marketId, zonesData, disableOldZones) {
    const maxSizeInBytes = 80 * 1024; // 80kb
    const zonesArrays = splitArrays(zonesData, maxSizeInBytes);
    const zonesNames = zonesData.map((zone) => zone.name);

    const promises = zonesArrays.map((array) => axiosInstance.post(
      `${baseUrl}/pickupZones/bulkUpload/`,
      { marketId, zonesData: array, zonesNames, disableOldZones },
    ));

    return Promise.all(promises).then((results) => {
      const result = { successes: [], errors: [] };

      results.forEach((r) => {
        result.successes = result.successes.concat(r.data.successes);
        result.errors = result.errors.concat(r.data.errors);
      });

      return result;
    });
  },

  async setActiveWrangler(wranglerId) {
    const data = await axiosInstance
      .post(`${baseUrl}/user/login`, { wrangler_id: wranglerId })
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async logout() {
    const data = await axiosInstance
      .get(`${baseUrl}/user/logout`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async getWranglers(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/wranglers?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async createWrangler(wrangler) {
    return axiosInstance.post(`${baseUrl}/wranglers/`, wrangler);
  },

  async updateWrangler(newWrangler, wranglerId) {
    return axiosInstance.put(`${baseUrl}/wranglers/${wranglerId}`, newWrangler);
  },

  async getStats(marketIds) {
    const data = await axiosInstance
      .post(`${baseUrl}/stats`, marketIds)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },

  async getRepairTickets(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    const data = await axiosInstance
      .get(`${baseUrl}/repairTickets?${qs}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
    return data;
  },
  async createRepairTicket(newRepairTicket) {
    return axiosInstance
      .post(`${baseUrl}/repairTickets/`, { newRepairTicket, user })
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },
  async updateRepairTicket(newRepairTicket, repairTicketId) {
    return axiosInstance
      .put(`${baseUrl}/repairTickets/${repairTicketId}`, { newRepairTicket, user })
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },
  async createTicketsForCases(caseData, marketId) {
    return axiosInstance.post(`${baseUrl}/repairTickets/bulkForCases`, {
      caseData, marketId, user, userId,
    });
  },
  getRouteById(routeId) {
    return axiosInstance.get(`${baseUrl}/routes/${routeId}`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
      });
  },
  async getRoutesForRides(rideIds) {
    return axiosInstance
      .post(`${baseUrl}/routes/getForRides?`, { rideIds })
      .then((response) => response.data);
  },

  async getRuns(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    return axiosInstance.get(`${baseUrl}/runs?${qs}`);
  },
  async getRunTasks(runId) {
    return axiosInstance.get(`${baseUrl}/runs/${runId}/runTasks`);
  },
  async createRun(run) {
    return axiosInstance.post(`${baseUrl}/runs/`, run);
  },
  async createDynamicRun(run) {
    return axiosInstance.post(`${baseUrl}/runs/dynamic`, run);
  },
  async updateRun(runId, newRun) {
    return axiosInstance.put(`${baseUrl}/runs/${runId}`, { newRun, user });
  },
  async deleteRun(runId) {
    return axiosInstance.delete(`${baseUrl}/runs/${runId}`);
  },
  async getRunSchedules(query) {
    let qs = '';
    if (query) {
      qs = objectToQueryString(query);
    }
    return axiosInstance.get(`${baseUrl}/runs/runSchedules?${qs}`);
  },
  async createRunSchedule(runSchedule) {
    return axiosInstance.post(`${baseUrl}/runs/runSchedules/`, runSchedule);
  },
  async updateRunSchedule(runScheduleId, newRunSchedule) {
    return axiosInstance.put(`${baseUrl}/runs/runSchedules/${runScheduleId}`, newRunSchedule);
  },
  async deleteRunSchedule(runScheduleId) {
    return axiosInstance.delete(`${baseUrl}/runs/runSchedules/${runScheduleId}`);
  },
  async getFeatureFlags(marketId) {
    return axiosInstance.get(`${baseUrl}/featureFlags/${marketId}`);
  },
  async bulkUpdateFlagStatus(arrayOfFlagObjects) {
    return axiosInstance.put(`${baseUrl}/featureFlags/batchUpdate`, arrayOfFlagObjects);
  },
};
